<template>
  <!-- Invisible component - no visual elements -->
  <div style="display: none;"></div>
</template>

<script>
export default {
  name: 'NFCScanner',
  data() {
    return {
      scanBuffer: '',
      scanTimeout: null,
      isScanning: false,
      scanDelay: 100, // milliseconds to wait after last keypress before processing
      minScanLength: 4, // minimum characters for a valid scan
      maxScanLength: 50, // maximum characters for a valid scan
      enabled: true, // Can be disabled when needed
      lastKeyTime: 0
    }
  },
  mounted() {
    this.initializeScanner();
    this.$emit('scanner-ready');
  },
  beforeDestroy() {
    this.cleanupScanner();
  },
  methods: {
    initializeScanner() {
      // Add global keydown listener
      document.addEventListener('keydown', this.handleKeyInput, true);
      document.addEventListener('keyup', this.handleKeyUp, true);
    },

    cleanupScanner() {
      // Remove event listeners
      document.removeEventListener('keydown', this.handleKeyInput, true);
      document.removeEventListener('keyup', this.handleKeyUp, true);
      
      // Clear any pending timeout
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
      }
    },

    handleKeyInput(event) {
      // Check if scanner is enabled
      if (!this.enabled) {
        return;
      }

      // Check if user is typing in form inputs or modal areas
      const activeElement = document.activeElement;
      const isTypingInInput = activeElement && this.isInputElement(activeElement);

      if (isTypingInInput) {
        return; // Don't interfere with normal typing
      }

      // Check if we're in a modal or specific areas where we should ignore input
      if (this.isInRestrictedArea(event.target)) {
        return;
      }

      const currentTime = Date.now();
      
      // Timing-based detection: NFC scanners type much faster than humans
      if (this.lastKeyTime && (currentTime - this.lastKeyTime) > 200) {
        // Too slow for NFC scanner, reset and ignore
        this.resetScanner();
        return;
      }
      
      this.lastKeyTime = currentTime;

      // Detect rapid successive key inputs (typical of NFC scanner)
      if (!this.isScanning) {
        this.startScanning();
      }

      // Handle special keys
      if (event.key === 'Enter') {
        event.preventDefault();
        this.processScan();
        return;
      }

      // Handle backspace/delete
      if (event.key === 'Backspace' || event.key === 'Delete') {
        this.scanBuffer = this.scanBuffer.slice(0, -1);
        return;
      }

      // Only capture printable characters
      if (event.key.length === 1) {
        event.preventDefault(); // Prevent normal input
        this.scanBuffer += event.key;
        
        // Reset the processing timeout
        this.resetScanTimeout();
      }
    },

    handleKeyUp(event) {
      // Additional handling if needed for key up events
      if (this.isScanning && event.key.length === 1 && !this.isInputElement(document.activeElement)) {
        event.preventDefault();
      }
    },

    isInputElement(element) {
      if (!element) return false;
      
      const inputTags = ['INPUT', 'TEXTAREA', 'SELECT'];
      const isFormControl = inputTags.includes(element.tagName);
      const isContentEditable = element.contentEditable === 'true';
      const hasFormClass = element.classList.contains('form-control') || 
                          element.classList.contains('form__field') ||
                          element.closest('.form-control') ||
                          element.closest('.form__field');
      
      return isFormControl || isContentEditable || hasFormClass;
    },

    isInRestrictedArea(element) {
      if (!element) return false;
      
      // Check if we're in specific areas where we should disable scanning
      const restrictedSelectors = [
        '.modal', // Any modal
        '.invoice', // Invoice area
        '.payment', // Payment area
        '.form', // Any form
        '[role="dialog"]', // ARIA dialogs
        '.a-modal', // Ant Design modals
      ];

      return restrictedSelectors.some(selector => {
        return element.closest && element.closest(selector);
      });
    },

    startScanning() {
      this.isScanning = true;
      this.scanBuffer = '';
      this.$emit('scan-started');
    },

    resetScanTimeout() {
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
      }
      
      this.scanTimeout = setTimeout(() => {
        this.processScan();
      }, this.scanDelay);
    },

    processScan() {
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
        this.scanTimeout = null;
      }

      const scannedValue = this.scanBuffer.trim();
      
      // Validate scan
      if (this.isValidScan(scannedValue)) {
        this.$emit('nfc-scanned', {
          value: scannedValue,
          timestamp: new Date(),
          length: scannedValue.length
        });
        this.$emit('scan-success', scannedValue);
      } else if (scannedValue.length > 0) {
        // Emit invalid scan event
        this.$emit('scan-error', {
          value: scannedValue,
          reason: this.getValidationError(scannedValue),
          timestamp: new Date()
        });
      }

      // Reset scanner state
      this.resetScanner();
    },

    isValidScan(value) {
      if (!value || typeof value !== 'string') return false;
      if (value.length < this.minScanLength) return false;
      if (value.length > this.maxScanLength) return false;
      
      // Additional validation can be added here
      // For example, check for specific patterns or formats
      return true;
    },

    getValidationError(value) {
      if (!value) return 'Empty scan';
      if (value.length < this.minScanLength) return `Too short (minimum ${this.minScanLength} characters)`;
      if (value.length > this.maxScanLength) return `Too long (maximum ${this.maxScanLength} characters)`;
      return 'Invalid format';
    },

    resetScanner() {
      this.isScanning = false;
      this.scanBuffer = '';
      this.lastKeyTime = 0;
      this.$emit('scan-ended');
    },

    // Public methods for parent component
    clearBuffer() {
      this.scanBuffer = '';
    },

    setMinLength(length) {
      this.minScanLength = Math.max(1, parseInt(length) || 1);
    },

    setMaxLength(length) {
      this.maxScanLength = Math.max(this.minScanLength, parseInt(length) || 50);
    },

    setScanDelay(delay) {
      this.scanDelay = Math.max(50, parseInt(delay) || 100);
    },

    getCurrentBuffer() {
      return this.scanBuffer;
    },

    // Methods to enable/disable the scanner
    enableScanner() {
      this.enabled = true;
      this.$emit('scanner-enabled');
    },

    disableScanner() {
      this.enabled = false;
      this.resetScanner();
      this.$emit('scanner-disabled');
    },

    isEnabled() {
      return this.enabled;
    }
  }
}
</script>

<style scoped>
/* No styles needed for invisible component */
</style>